import {useRef,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArrowUp,FiMic,FiMicOff,FiPlus,FiPaperclip,FiX} = FiIcons;

// The Shared Chat Input (docs/screen-capability-and-layout-spec.md →
// Cross-Cutting Patterns). One composer for every chat-capable surface:
//   • '+' attachments — handed to the host (real KB pipeline per surface; this
//     component never uploads anywhere itself)
//   • mic = browser-native speech-to-text DICTATION only (never a live voice
//     mode — that feature is deliberately deferred platform-wide)
//   • right-aligned BOT SWITCHER — which bot on this roster you're addressing,
//     defaulting to the team Lead. Deliberately NOT a model selector, and no
//     reasoning-depth/effort control (model_tier already covers it).
export default function ChatInput({
  onSubmit,
  disabled=false,
  busy=false,
  placeholder='Ask anything…',
  botOptions=[],
  botId=null,
  lead=null,
  onBotChange=null,
  allowAttachments=true,
  compact=false
}) {
  const [text,setText]=useState('');
  const [attachments,setAttachments]=useState([]);
  const [listening,setListening]=useState(false);
  const [notice,setNotice]=useState(null);
  const fileRef=useRef(null);

  const SpeechRecognition=(
    typeof window!=='undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition)
  ) || null;

  function startDictation() {
    if (!SpeechRecognition) {
      setNotice('Dictation requires browser speech recognition support.');
      setTimeout(()=> setNotice(null), 3500);
      return;
    }

    try {
      const recognition=new SpeechRecognition();
      recognition.lang='en-US';
      recognition.interimResults=false;
      recognition.maxAlternatives=1;

      recognition.onresult=(event)=> {
        const transcript=event.results?.[0]?.[0]?.transcript || '';
        if (transcript) {
          setText((current)=> [current.trim(),transcript.trim()].filter(Boolean).join(' '));
        }
      };
      recognition.onend=()=> setListening(false);
      recognition.onerror=()=> {
        setListening(false);
        setNotice('Dictation could not capture audio.');
        setTimeout(()=> setNotice(null), 3500);
      };

      setListening(true);
      recognition.start();
    } catch {
      setListening(false);
      setNotice('Unable to start dictation.');
      setTimeout(()=> setNotice(null), 3500);
    }
  }

  function stopDictation() {
    setListening(false);
  }

  function onPickFiles(event) {
    const picked=[...(event.target.files || [])];
    if (!picked.length) return;

    setAttachments((current)=> [
      ...current,
      ...picked.filter((file)=> !current.some((item)=> item.name===file.name && item.size===file.size))
    ]);
    event.target.value='';
  }

  function removeAttachment(name) {
    setAttachments((current)=> current.filter((file)=> file.name!==name));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const prompt=text.trim();
    if ((!prompt && !attachments.length) || busy || disabled) return;

    const files=[...attachments];
    setText('');
    setAttachments([]);

    try {
      await onSubmit(prompt,files,effectiveBotId);
    } catch (err) {
      // Host surfaces own error display; never crash the composer on failure.
      if (typeof err==='string' || err instanceof Error) {
        setText(prompt);
        setAttachments(files);
      }
    }
  }

  const dictationHint=SpeechRecognition
    ? (listening ? 'Stop dictation' : 'Dictate with your microphone')
    : 'Dictation is not supported in this browser';

  const effectiveBotId=botId || (botOptions[0]?.id || lead?.id || '');

  return (
    <div className={`chat-input-wrapper ${compact ? 'compact' : ''}`}>
      {notice && (
        <div className="chat-input-notice">
          <small>{notice}</small>
          <button type="button" onClick={()=> setNotice(null)} aria-label="Dismiss">
            <SafeIcon icon={FiX} />
          </button>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="chat-attachment-tray">
          {attachments.map((file)=> (
            <span className="attachment-pill" key={`${file.name}-${file.size}`}>
              <SafeIcon icon={FiPaperclip} />
              <span className="att-name">{file.name}</span>
              <button
                type="button"
                className="att-remove"
                onClick={()=> removeAttachment(file.name)}
                title={`Remove ${file.name}`}
                aria-label={`Remove ${file.name}`}
              >
                <SafeIcon icon={FiX} />
              </button>
            </span>
          ))}
          <small className="hint-copy">Feeds Knowledge Base</small>
        </div>
      )}

      <form className="chat-input chat-composer" onSubmit={handleSubmit}>
        {allowAttachments && (
          <>
            <button
              type="button"
              className="chat-action-btn attach-btn"
              title="Attach a file to the knowledge base"
              aria-label="Attach a file"
              onClick={()=> fileRef.current?.click()}
              disabled={disabled || busy}
            >
              <SafeIcon icon={FiPlus} />
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              style={{display:'none'}}
              onChange={onPickFiles}
            />
          </>
        )}

        <button
          type="button"
          className={`chat-action-btn mic-btn ${listening ? 'listening' : ''}`}
          title={dictationHint}
          aria-label={dictationHint}
          onClick={listening ? stopDictation : startDictation}
          disabled={disabled || busy}
        >
          <SafeIcon icon={listening ? FiMicOff : FiMic} />
        </button>

        <input
          className="chat-input-field"
          value={text}
          onChange={(event)=> setText(event.target.value)}
          placeholder={placeholder}
          disabled={disabled || busy}
        />

        {botOptions.length > 1 ? (
          <div className="bot-switcher">
            <select
              value={effectiveBotId}
              onChange={(event)=> onBotChange?.(event.target.value)}
              disabled={disabled || busy}
              title="Select which bot to speak with"
              aria-label="Bot switcher"
            >
              {botOptions.map((bot)=> (
                <option key={bot.id} value={bot.id}>
                  {bot.name} {bot.isLead ? '(Lead)' : `(${bot.role})`}
                </option>
              ))}
            </select>
          </div>
        ) : botOptions.length === 1 ? (
          <span className="bot-pill-static" title={`${botOptions[0].name} (${botOptions[0].role})`}>
            {botOptions[0].name}
          </span>
        ) : null}

        <button
          type="submit"
          className="chat-send-btn"
          disabled={busy || disabled || (!text.trim() && !attachments.length)}
          aria-label="Send message"
        >
          {busy ? <span className="composer-busy">…</span> : <SafeIcon icon={FiArrowUp} />}
        </button>
      </form>
    </div>
  );
}
